import { useLocation, Link } from "wouter";
import { Wrench, ArrowUpRight, CornerUpLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ReportedTaskSummary } from "@shared/schema";

// Human-readable status label + a muted/active treatment. Terminal statuses
// render muted (gray) since they need no action; active ones get color so an
// open report stands out in the timeline.
const STATUS_LABELS: Record<string, string> = {
  requests: "New request",
  open: "Open",
  assigned: "Assigned",
  accepted: "Accepted",
  in_progress: "In progress",
  on_hold: "On hold",
  needs_attention: "Needs attention",
  completed: "Completed",
  awaiting_approval: "Awaiting approval",
  approved: "Approved",
  paid: "Paid",
  rejected: "Rejected",
  cancelled: "Cancelled",
};

const TERMINAL_STATUSES = new Set([
  "completed",
  "complete",
  "approved",
  "paid",
  "rejected",
  "cancelled",
]);

function statusBadgeClass(status: string): string {
  if (TERMINAL_STATUSES.has(status)) return "bg-gray-100 text-gray-600";
  if (status === "needs_attention") return "bg-red-100 text-red-700";
  if (status === "on_hold" || status === "awaiting_approval") return "bg-amber-100 text-amber-800";
  if (status === "requests" || status === "open") return "bg-orange-100 text-orange-800";
  return "bg-blue-100 text-blue-700"; // assigned / accepted / in_progress
}

const PRIORITY_CLASS: Record<string, string> = {
  urgent: "bg-red-100 text-red-700",
  high: "bg-orange-100 text-orange-700",
  medium: "bg-amber-100 text-amber-800",
  low: "bg-gray-100 text-gray-600",
  batch: "bg-gray-100 text-gray-600",
};

export function ReportedTaskCard({
  task,
  memberName,
  onReply,
}: {
  task: ReportedTaskSummary;
  memberName?: string | null;
  onReply?: () => void;
}) {
  const [, navigate] = useLocation();
  const statusLabel = STATUS_LABELS[task.status] ?? task.status;
  const submittedBy = memberName ? `Reported by ${memberName}` : "Reported by this member";

  return (
    <div className="my-2 flex justify-center" data-testid={`card-reported-task-${task.id}`}>
      <div className="w-full max-w-md rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 shadow-sm">
        <div className="flex items-start gap-2.5">
          <div className="mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <Wrench className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                Maintenance task
              </span>
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${statusBadgeClass(task.status)}`}
                data-testid={`badge-task-status-${task.id}`}
              >
                {statusLabel}
              </span>
            </div>
            <p
              className="mt-1 break-words text-sm font-medium text-gray-900"
              data-testid={`text-task-title-${task.id}`}
            >
              {task.title}
            </p>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500">
              <span
                className={`rounded px-1.5 py-0.5 font-medium capitalize ${PRIORITY_CLASS[task.priority] ?? "bg-gray-100 text-gray-600"}`}
              >
                {task.priority}
              </span>
              <span data-testid={`text-task-reporter-${task.id}`}>{submittedBy}</span>
            </div>
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                className="h-7 bg-amber-600 px-2.5 text-xs text-white hover:bg-amber-700"
                asChild
                data-testid={`button-view-task-${task.id}`}
              >
                <Link href={`/app/tasks?task=${task.id}`}><ArrowUpRight className="mr-1 h-3.5 w-3.5" /> View task</Link>
              </Button>
              {onReply && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 border-amber-300 px-2.5 text-xs text-amber-800 hover:bg-amber-100"
                  onClick={onReply}
                  data-testid={`button-reply-task-${task.id}`}
                >
                  <CornerUpLeft className="mr-1 h-3.5 w-3.5" /> Respond here
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
